import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';

import SecondaryInput from '@/components/ui/custom/secondary/SecondaryInput';
import { stringAnswer } from '@/utils/_deprecated/activities';
import { CourseStudioFormFields } from '../CourseStudio.hooks';
import { AutoFocusRef } from '@/data/hook/useAutoFocus';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import { StudioPermissions } from '@/enums/VersioningStatus';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import { TrashIcon } from '@heroicons/react/24/outline';

type Props = {
  blockIndex: number;
  lessonIndex: number;
  chapterIndex: number;
  subChapterIndex: number;
  removeSubChapter: () => void;
  setSubChapterElementRef: (index: number) => (el: AutoFocusRef) => void;
  studioPermissions: StudioPermissions;
};

export default function LessonBookSubChapter(props: Props) {
  const {
    blockIndex,
    lessonIndex,
    chapterIndex,
    subChapterIndex,
    removeSubChapter,
    setSubChapterElementRef,
    studioPermissions,
  } = props;

  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio',
  });

  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const {
    control,
    formState: { errors },
  } = useFormContext<CourseStudioFormFields>();

  const placeholder = useMemo(() => {
    const formatted = t('subchapterPlaceholder', {
      letter: stringAnswer(subChapterIndex).toLowerCase(),
    });

    return formatted;
  }, [subChapterIndex, t]);

  const disabled = useMemo(
    () =>
      !studioPermissions.allowOtherSteps.simpleEditing &&
      !studioPermissions.allowOtherSteps.advancedEditing,
    [studioPermissions],
  );

  return (
    <li>
      <Controller
        control={control}
        name={`lessonBlocks.${blockIndex}.lessons.${lessonIndex}.chapters.${chapterIndex}.items.${subChapterIndex}.title`}
        rules={{
          required: {
            value: true,
            message: tCommon('form.required', {
              field: tCommon('subchapter').toLowerCase(),
            }),
          },
        }}
        render={({ field: { onChange, value = '' } }) => (
          <ConditionalWrapper
            condition={disabled}
            wrapper={
              <Tooltip
                text={t('notAllowedIfNotAtLeastInLimitedEdition')}
                classNameContainer="flex w-full"
              />
            }
          >
            <SecondaryInput
              ref={setSubChapterElementRef(subChapterIndex)}
              value={value}
              placeholder={placeholder}
              onChange={onChange}
              disabled={disabled}
              error={
                errors.lessonBlocks?.[blockIndex]?.lessons?.[lessonIndex]
                  ?.chapters?.[chapterIndex]?.items?.[subChapterIndex]?.title
                  ?.message
              }
              className={{
                label: 'font-500',
                input: 'border-base-content/25 font-400',
              }}
              clearWrapper={
                <ConditionalWrapper
                  condition={!studioPermissions.allowOtherSteps.advancedEditing}
                  wrapper={
                    <Tooltip
                      text={t('notAllowedLimitedEditing')}
                      classNameContainer="flex"
                    />
                  }
                >
                  <IconButton
                    disabled={
                      !studioPermissions.allowOtherSteps.advancedEditing
                    }
                    type="button"
                    icon={
                      <TrashIcon className="text-error w-5 h-5 stroke-[1.5] hover:text-error/50 transition-all duration-200 ease-in-out" />
                    }
                    onClick={removeSubChapter}
                  />
                </ConditionalWrapper>
              }
            />
          </ConditionalWrapper>
        )}
      />
    </li>
  );
}
